import React from 'react';

import { Document, Page, StyleSheet } from '@react-pdf/renderer';

import { gerarCodigoBarrasDanfe } from '../assets/codigoBarrasDanfe';
import { registrarFontesDanfe } from '../assets/fontesDanfe';
import { mapearDanfe } from '../mapeamento/danfeMapper';
import {
  Cabecalho,
  CalculoImposto,
  Canhoto,
  Destinatario,
  Fatura,
  Produtos,
  Rodape,
  Transporte,
} from './blocos';
import { CORES } from './grade';
import { distribuirItensEmFolhas } from './paginacao';

/**
 * @file ./views/layouts/danfe/producao/danfeProducao.jsx
 *
 * **Template de produção do DANFE** — NF-e modelo 55, A-4 retrato, folhas
 * soltas (MOC 7.0 – Anexo II, Anexo III.02).
 *
 * Recebe o JSON estrutural devolvido por `converterXmlNfeParaJson` (projeto
 * NFe) e devolve um componente de documento pronto para o `PDFViewer` ou
 * para `pdf()`/`renderToStream`.
 *
 * ## Pipeline
 *
 * ```
 * XML da SEFAZ
 *   → converterXmlNfeParaJson()   projeto NFe — parsing
 *   → mapearDanfe()               mapper — DTO de visualização
 *   → distribuirItensEmFolhas()   paginação — folhas, itens e coordenadas
 *   → danfeProducao()             este arquivo — montagem
 * ```
 *
 * ## Por que `padding: 0` na página
 *
 * A grade é herdada de um gerador `pdfkit`, onde `doc.rect(x, y, l, a)` é
 * medido a partir da **origem da página** e ignora as margens. Reproduzir
 * isso exige que a `<Page>` não tenha recuo próprio: as margens do MOC
 * (item 3.6.2) já estão embutidas nas coordenadas da grade.
 *
 * ## Distribuição entre folhas
 *
 * Cabeçalho de identificação e rodapé de dados adicionais saem em **todas**
 * as folhas — o item 3.5 do manual exige a repetição do cabeçalho, e o
 * 3.10.2 exige o número da folha em todas elas, inclusive quando há uma só.
 * Canhoto, destinatário, fatura, cálculo do imposto e transportador ficam
 * apenas na primeira, porque o mesmo item 3.5 restringe a área das folhas
 * adicionais aos itens que não couberam e às informações complementares.
 *
 * @example
 * import { converterXmlNfeParaJson } from '.../converterXmlNfeParaJson.js';
 *
 * const { sucesso, dadosNfe } = converterXmlNfeParaJson(xml);
 * if (sucesso) {
 *   const Documento = danfeProducao(dadosNfe);
 *   // <PDFViewer><Documento /></PDFViewer>
 * }
 */

const estilos = StyleSheet.create({
  pagina: {
    backgroundColor: CORES.papel,
    padding: 0,
  },
});

/**
 * Gera o código de barras da chave, tolerando ambientes sem `canvas`.
 *
 * A geração roda no browser (jsbarcode desenha num `<canvas>`). Numa
 * renderização server-side o chamador injeta outra implementação por
 * `opcoes.gerarCodigoBarras`; sem nenhuma disponível, o quadro é impresso
 * com o aviso no lugar das barras em vez de derrubar o documento inteiro.
 *
 * @param {string} chave
 * @param {Function} gerador
 * @returns {{ src: string }|null}
 */
function gerarCodigoBarrasComSeguranca(chave, gerador) {
  if (!chave) return null;

  try {
    return gerador(chave);
  } catch (erro) {
    console.warn(
      '[danfeProducao] Não foi possível gerar o código de barras da chave:',
      erro.message,
    );
    return null;
  }
}

/**
 * Monta o DANFE de produção.
 *
 * @param {Object} dadosNfe - `dadosNfe` devolvido por `converterXmlNfeParaJson`.
 * @param {Object} [opcoes]
 * @param {boolean} [opcoes.exibirCanhoto=true] - Imprime o canhoto destacável
 *   (MOC 3.3.1 permite suprimi-lo).
 * @param {Function} [opcoes.gerarCodigoBarras] - Gerador CODE-128C; injetável
 *   para renderização fora do browser.
 * @returns {React.FunctionComponent} Componente `<Document>` do react-pdf.
 * @throws {TypeError} Se `dadosNfe` não for o objeto esperado.
 */
export function danfeProducao(dadosNfe, opcoes = {}) {
  const {
    exibirCanhoto = true,
    gerarCodigoBarras = gerarCodigoBarrasDanfe,
  } = opcoes;

  registrarFontesDanfe();

  const danfe = mapearDanfe(dadosNfe);
  const { deslocamentoFatura, folhas } = distribuirItensEmFolhas(
    danfe.itens,
    danfe.cobranca.duplicatas,
  );
  const codigoBarras = gerarCodigoBarrasComSeguranca(
    danfe.chave.valor,
    gerarCodigoBarras,
  );

  const metadados = {
    title: `DANFE - NF-e ${danfe.documento.numero} - SÉRIE ${danfe.documento.serie}`,
    author: danfe.emitente.nome,
    subject: 'Documento Auxiliar da Nota Fiscal Eletrônica',
    keywords: `danfe nfe ${danfe.chave.valor}`,
    creator: 'PDFBuilder - Template de Produção DANFE (MOC 7.0, Anexo II)',
    producer: 'React-PDF Renderer',
  };

  /**
   * O react-pdf usa um reconciliador próprio: precisa receber um
   * componente para instanciar, não um elemento já construído.
   */
  const DocumentoDanfe = () => (
    <Document {...metadados}>
      {folhas.map((folha) => (
        <Page
          key={`folha-${folha.numero}`}
          size="A4"
          orientation="portrait"
          /* A paginação é decidida por `distribuirItensEmFolhas`; a
                       quebra automática do renderer só poderia contrariá-la. */
          wrap={false}
          style={estilos.pagina}
        >
          {folha.primeira && exibirCanhoto ? <Canhoto danfe={danfe} /> : null}

          <Cabecalho
            danfe={danfe}
            folha={folha}
            codigoBarras={codigoBarras}
            primeiraFolha={folha.primeira}
          />

          {folha.primeira ? <Destinatario danfe={danfe} /> : null}
          {folha.primeira ? <Fatura danfe={danfe} /> : null}
          {folha.primeira ? (
            <CalculoImposto danfe={danfe} deslocamento={deslocamentoFatura} />
          ) : null}
          {folha.primeira ? (
            <Transporte danfe={danfe} deslocamento={deslocamentoFatura} />
          ) : null}

          <Produtos
            linhas={folha.linhas}
            topoTitulo={folha.topoTitulo}
            topoTabela={folha.topoTabela}
          />

          <Rodape danfe={danfe} />
        </Page>
      ))}
    </Document>
  );

  DocumentoDanfe.displayName = 'DanfeProducao';

  return DocumentoDanfe;
}

export default danfeProducao;
